import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  CircularProgress,
  Popper,
  TextField,
  Typography,
} from '@mui/material';
import type { PopperProps } from '@mui/material';

import { PLACES_AUTOCOMPLETE_LIMITS } from '@bandido/shared/types';
import type { PlaceKind, RuteroVertical } from '@bandido/shared/types';

import { useDebouncedState } from '@frontend/features/common/hooks/useDebouncedState';
import { connectorCapabilitiesAreActionable } from '@frontend/features/rutero/connectorCapabilitiesQueryKey';
import { useAutocompleteFocus } from '../useAutocompleteFocus';
import {
  apiErrorMessage,
  ConnectorInfo,
  usePlacesAutocomplete,
  useConnectorCapabilities,
} from '../queries';
import { deriveServedByForVertical } from '../connectorCapabilityResolution';
import {
  PlaceOption,
  PlaceRef,
  flattenPlaceOptions,
  placeOptionGroupLabelKey,
  placesNoticeReason,
  sortPlaceOptionsForDisplay,
  toPlaceRef,
} from '../places';
import { PlaceOptionRow } from './PlaceOptionRow';

const POPPER_WIDTH = 640;

/**
 * El desplegable del maestro de lugares: más ancho que el campo, porque una
 * fila lleva nombre, miga y chips. Exportado para que el campo unificado de
 * puntos de traslado (`TransferPointAutocomplete`, #2227) dibuje la MISMA
 * lista y no una parecida.
 */
export function PlaceAutocompletePopper({ sx, style, ...props }: PopperProps) {
  return (
    <Popper
      {...props}
      placement="bottom-start"
      // MUI calcula el ancho del popup desde el anchor y lo pasa inline. Ese
      // `style.width` le gana a `sx`, así que un campo de 200 px comprimía
      // nombre, miga y chips dentro de esos mismos 200 px. Conservamos el
      // resto del posicionamiento inline y dejamos el ancho bajo una sola
      // regla responsive común para todos los verticales.
      style={{ ...style, width: undefined }}
      sx={[
        {
          width: { xs: 'calc(100vw - 32px)', sm: POPPER_WIDTH },
          maxWidth: 'calc(100vw - 32px)',
          zIndex: (theme) => theme.zIndex.modal,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    />
  );
}

interface Props {
  label: string;
  /** Vertical de rutero (`'rail'`, `'ferry'`, …): deriva `servedBy`, nunca hardcodeado acá. */
  vertical: RuteroVertical;
  value: PlaceRef | null;
  onChange: (place: PlaceRef | null) => void;
  kinds?: PlaceKind[];
  countries?: string[];
  group?: boolean;
  width?: number | string;
  required?: boolean;
}

/**
 * Selector genérico de destinos contra el maestro de lugares (ubikeishon,
 * `plc_*`). Reemplaza el picker nativo por vertical: `servedBy` sale de
 * `GET /rutero/connectors` (nunca hardcodeado — 21.1 aplicado a datos), y
 * sin esa autoridad cargada el picker queda deshabilitado (fail-closed, no
 * "sin filtro"). Sin texto libre: si un lugar no está en la respuesta, no
 * se puede elegir (decisión de ubikeishon, 2026-08-18).
 *
 * La lógica pura (`PlaceRef`, conversión, aplanado de `children`, resaltado
 * de `matched`) vive en `../places.ts`, no acá — este archivo es `.tsx` y el
 * jest de este repo NO resuelve/testea `.tsx` a propósito (ver
 * `jest.config.cjs`); todo lo testeable tiene que poder importarse desde un
 * `.ts` sin arrastrar React.
 */
export function PlaceAutocomplete({
  label,
  vertical,
  value,
  onChange,
  kinds,
  countries,
  group,
  width = 280,
  required,
}: Props) {
  const { t } = useTranslation();
  const [debouncedQuery, setQuery] = useDebouncedState('', 250);

  const {
    data: connectors,
    isSuccess: capabilitiesLoaded,
    isFetching: capabilitiesFetching,
  } = useConnectorCapabilities();
  const capabilitiesActionable = connectorCapabilitiesAreActionable({
    isSuccess: capabilitiesLoaded,
    isFetching: capabilitiesFetching,
  });
  const servedBy = useMemo(
    () =>
      deriveServedByForVertical(
        connectors as ConnectorInfo[] | undefined,
        vertical,
      ),
    [connectors, vertical],
  );
  const pickerReady = capabilitiesActionable && servedBy.length > 0;

  const {
    data,
    isFetching: searching,
    isError: searchFailed,
    error: searchError,
  } = usePlacesAutocomplete(debouncedQuery, {
    kinds,
    servedBy,
    countries,
    group,
    enabled: pickerReady,
  });

  const options = useMemo(
    () => sortPlaceOptionsForDisplay(flattenPlaceOptions(data?.results ?? [])),
    [data?.results],
  );

  // El value controlado por MUI necesita el shape de `options`
  // (`PlaceOption`); se reconstruye lo mínimo desde el `PlaceRef` guardado
  // (mismo patrón que `entryFromParams` en los buscadores existentes) — no
  // hace falta que esté en la lista actual para mostrarse seleccionado, ni
  // que lleve `nested` (un value elegido nunca se dibuja indentado).
  // MEMOIZADO sobre los campos primitivos (no la referencia del objeto):
  // `useAutocomplete` compara `value` con el `value` del render anterior POR
  // REFERENCIA para decidir si el usuario cambió la selección; un objeto
  // reconstruido inline en cada render rompe esa comparación y el input se
  // resetea al nombre del lugar seleccionado en cada tecla, sin dejar
  // editarlo.
  const selected = useMemo<PlaceOption | null>(
    () =>
      value
        ? {
          id: value.id,
          kind: value.kind,
          name: value.name,
          ...(value.code ? { code: value.code } : {}),
        }
        : null,
    [value?.id, value?.kind, value?.name, value?.code],
  );

  // La etiqueta del input fuera de la sesión de tipeo tiene que ser la MISMA
  // que `getOptionLabel` (abajo): si difieren, MUI repone el input por su
  // cuenta contra la suya y pisa lo que muestra este componente.
  const focus = useAutocompleteFocus({
    selectedLabel: selected?.name ?? '',
    onQueryChange: setQuery,
  });

  const noticeReason = placesNoticeReason({
    ready: pickerReady,
    queryLength: debouncedQuery.trim().length,
    minLength: PLACES_AUTOCOMPLETE_LIMITS.queryMinLength,
    isError: searchFailed,
  });
  // Aislado (#2226): el spinner de ESTE campo se enciende solo mientras el
  // asesor tipea acá. Sin `focus.editing` seguiría prendido los 250ms de
  // debounce que tarda la query en vaciarse después de salir del campo.
  const showLoading =
    focus.editing &&
    searching &&
    debouncedQuery.trim().length >= PLACES_AUTOCOMPLETE_LIMITS.queryMinLength;

  return (
    <Autocomplete
      size="small"
      sx={{ width: '100%', maxWidth: width }}
      options={options}
      loading={showLoading}
      disabled={!pickerReady}
      value={selected}
      onChange={(_event, place, reason) => {
        if (focus.commitValueChange(reason)) {
          onChange(place ? toPlaceRef(place) : null);
        }
      }}
      inputValue={focus.inputValue}
      onFocus={focus.onFocus}
      onBlur={focus.onBlur}
      onInputChange={focus.onInputChange}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, sel) => option.id === sel.id}
      // El ranking lo hace ubikeishon: no re-filtrar por el label.
      filterOptions={(opts) => opts}
      PopperComponent={PlaceAutocompletePopper}
      ListboxProps={{
        sx: {
          py: 0,
          '& .MuiAutocomplete-option': {
            minHeight: 64,
            alignItems: 'stretch',
            px: 2,
            py: 1,
          },
        },
      }}
      groupBy={(option) => t(placeOptionGroupLabelKey(option))}
      renderGroup={(params) => (
        <Box component="li" key={params.key}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              position: 'sticky',
              top: 0,
              zIndex: 1,
              px: 2,
              pt: 1,
              pb: 0.5,
              fontWeight: 700,
              letterSpacing: 0,
              bgcolor: 'background.paper',
            }}
          >
            {params.group}
          </Typography>
          <Box component="ul" sx={{ p: 0, m: 0 }}>
            {params.children}
          </Box>
        </Box>
      )}
      renderOption={(props, option) => {
        // `nested`, no `parent`: el contrato trae `parent` en TODO lo que no
        // sea un país, así que deducirlo de ahí indentaría también los
        // resultados de primer nivel (ver `PlaceOption` en `../places.ts`).
        const isChild = Boolean(option.nested);
        return (
          <Box
            component="li"
            {...props}
            key={option.id}
            sx={isChild ? { pl: 5 } : undefined}
          >
            <PlaceOptionRow option={option} />
          </Box>
        );
      }}
      // `disabled` (MUI no deja abrir el popup) y `error` (el picker sigue
      // habilitado, así que ESTA rama sí es alcanzable) también se cubren acá
      // por si el popup llega a abrirse en un estado intermedio; la fuente
      // visible siempre es el `helperText` de abajo, que no depende del popup.
      noOptionsText={
        noticeReason === 'disabled'
          ? t('travelServices.places.noProviders')
          : noticeReason === 'error'
            ? (apiErrorMessage(searchError) ?? t('travelServices.places.searchError'))
            : noticeReason === 'tooShort'
              ? t('travelServices.places.typeToSearch', {
                count: PLACES_AUTOCOMPLETE_LIMITS.queryMinLength,
              })
              : t('travelServices.places.noResults')
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={t('travelServices.places.placeholder')}
          required={required}
          error={noticeReason === 'error'}
          // Visible aunque el Autocomplete esté `disabled` (a diferencia de
          // `noOptionsText`, que solo se ve con el popup abierto — y MUI no
          // deja abrir el popup de un control disabled).
          helperText={
            noticeReason === 'disabled'
              ? t('travelServices.places.noProviders')
              : noticeReason === 'error'
                ? (apiErrorMessage(searchError) ?? t('travelServices.places.searchError'))
                : undefined
          }
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {showLoading && <CircularProgress size={16} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
